import type { TPlayerRemoteFrame, TPlayerRemoteFrameAck } from '@lucimark/shared/models/player-remote-control'
import { PLAYER_REMOTE_FRAME_KEEPALIVE_MS } from '@lucimark/shared/models/player-remote-control'

/** After this many replies saying nobody is watching, stop streaming (the operator closed the tab). */
export const REMOTE_FRAME_ZERO_VIEWER_STOP_AFTER = 3
/** Consecutive transport failures (network / 5xx / 429) before giving up. */
export const REMOTE_FRAME_MAX_CONSECUTIVE_ERRORS = 5
/** Consecutive empty screenshots (Accessibility off, screen asleep) before giving up. */
export const REMOTE_FRAME_MAX_CONSECUTIVE_CAPTURE_FAILURES = 10
/** Extra wait after a 429, on top of the normal interval. */
export const REMOTE_FRAME_RATE_LIMIT_BACKOFF_MS = 5_000

export interface IRemoteFrameCapture {
  jpegBase64: string
  width?: number
  height?: number
}

/** Thrown by `post` implementations; `httpStatus` decides whether the stream can continue. */
export interface IRemoteFramePostError {
  httpStatus?: number
  retryAfterMs?: number
}

export interface IRemoteFrameStreamerDeps {
  capture: () => Promise<IRemoteFrameCapture | null>
  post: (frame: TPlayerRemoteFrame) => Promise<TPlayerRemoteFrameAck>
  now?: () => number
  sleep?: (ms: number) => Promise<void>
  onStop?: (reason: TRemoteFrameStreamStopReason) => void
}

export type TRemoteFrameStreamStopReason =
  | 'stopped'
  | 'session_ended'
  | 'no_viewers'
  | 'server_rejected'
  | 'errors'
  | 'capture_failed'

/** FNV-1a over the base64 string, plus its length: a static screen encodes to the same JPEG every time. */
export function hashFrame(jpegBase64: string): string {
  let h = 0x811C9DC5
  for (let i = 0; i < jpegBase64.length; i++) {
    h ^= jpegBase64.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return `${jpegBase64.length}:${(h >>> 0).toString(16)}`
}

/**
 * Pushes Remote View frames from the player while a session is open, instead of the operator
 * requesting one command per frame. Frames are only uploaded when the screen changed; an
 * "unchanged" beat is sent at least every {@link PLAYER_REMOTE_FRAME_KEEPALIVE_MS} so the operator
 * can tell a static screen from a stalled stream. The next capture only starts after the previous
 * upload finished, so slow links never build a backlog.
 */
export function createRemoteFrameStreamer(deps: IRemoteFrameStreamerDeps) {
  const now = deps.now ?? (() => Date.now())
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms)))
  let generation = 0
  let running = false
  let onStopHandlers: Array<(reason: TRemoteFrameStreamStopReason) => void> = []

  function finish(gen: number, reason: TRemoteFrameStreamStopReason): void {
    if (gen !== generation) {
      return
    }
    running = false
    generation += 1
    deps.onStop?.(reason)
    for (const handler of onStopHandlers) {
      handler(reason)
    }
  }

  async function run(gen: number, intervalMs: number, untilMs: number): Promise<void> {
    let seq = 0
    let lastHash: string | null = null
    let lastSentAt = 0
    let zeroViewerStreak = 0
    let errorStreak = 0
    let captureFailStreak = 0
    let extraBackoffMs = 0

    while (gen === generation) {
      if (now() >= untilMs) {
        finish(gen, 'session_ended')
        return
      }
      const startedAt = now()
      try {
        const capture = await deps.capture()
        if (gen !== generation) {
          return
        }
        if (capture == null) {
          captureFailStreak += 1
          if (captureFailStreak >= REMOTE_FRAME_MAX_CONSECUTIVE_CAPTURE_FAILURES) {
            finish(gen, 'capture_failed')
            return
          }
        }
        else {
          captureFailStreak = 0
          const hash = hashFrame(capture.jpegBase64)
          const changed = hash !== lastHash
          const due = now() - lastSentAt >= PLAYER_REMOTE_FRAME_KEEPALIVE_MS
          if (changed || due) {
            const capturedAt = now()
            const frame: TPlayerRemoteFrame = changed
              ? {
                  seq: seq++,
                  capturedAt,
                  jpegBase64: capture.jpegBase64,
                  ...(capture.width != null ? { width: capture.width } : {}),
                  ...(capture.height != null ? { height: capture.height } : {})
                }
              : { seq: seq++, capturedAt, unchanged: true }
            const ack = await deps.post(frame)
            if (gen !== generation) {
              return
            }
            // Only remember the frame as delivered once the server accepted it, so a failed upload retries.
            lastHash = hash
            lastSentAt = capturedAt
            errorStreak = 0
            extraBackoffMs = 0
            zeroViewerStreak = ack.viewers === 0 ? zeroViewerStreak + 1 : 0
            if (zeroViewerStreak >= REMOTE_FRAME_ZERO_VIEWER_STOP_AFTER) {
              finish(gen, 'no_viewers')
              return
            }
          }
        }
      }
      catch (err) {
        if (gen !== generation) {
          return
        }
        const status = (err as IRemoteFramePostError | null)?.httpStatus
        if (status === 401 || status === 403 || status === 404 || status === 409) {
          finish(gen, 'server_rejected')
          return
        }
        errorStreak += 1
        if (errorStreak >= REMOTE_FRAME_MAX_CONSECUTIVE_ERRORS) {
          finish(gen, 'errors')
          return
        }
        if (status === 429) {
          extraBackoffMs = Math.max((err as IRemoteFramePostError).retryAfterMs ?? 0, REMOTE_FRAME_RATE_LIMIT_BACKOFF_MS)
        }
      }
      const elapsed = now() - startedAt
      await sleep(Math.max(0, intervalMs - elapsed) + extraBackoffMs)
      extraBackoffMs = 0
    }
  }

  return {
    /** Starts (or restarts, e.g. with a new interval) streaming until `untilMs`. */
    start(params: { intervalMs: number, untilMs: number }): void {
      // Bumping the generation makes any previous loop exit at its next checkpoint.
      running = true
      generation += 1
      void run(generation, params.intervalMs, params.untilMs)
    },
    /** Stops streaming; a no-op when idle. */
    stop(): void {
      if (running) {
        running = false
        generation += 1
      }
    },
    isRunning(): boolean {
      return running
    },
    /** Test/diagnostic hook: called with the reason whenever a stream ends on its own. */
    onStopped(handler: (reason: TRemoteFrameStreamStopReason) => void): void {
      onStopHandlers = [...onStopHandlers, handler]
    }
  }
}

export type TRemoteFrameStreamer = ReturnType<typeof createRemoteFrameStreamer>
